/*
 * Cell handling of an xlsx file.
 *
 * Nothing here knows what the file holds.
 * It works on the plain rows the parser gives.
 */

import { XlsxCellValue, XlsxRow } from '../types/xlsx';

const LETTERS_IN_ALPHABET = 26;
const FIRST_LETTER_CODE = 'A'.charCodeAt(0);

/** Thousands grouped with dots, e.g. "1.200" - unambiguous, since the groups are of three */
const DOT_GROUPED_THOUSANDS = /^\d{1,3}(\.\d{3})+$/;
/** Everything a number cannot hold: spaces, units ("Ft"), any other text */
const NON_NUMBER_CHARS = /[^\d,.\-+]/g;
const NUMBER_PATTERN = /^[-+]?\d*\.?\d+$/;

/**
 * What is written into a cell that has to stay empty.
 * A read cell is empty in more ways than this one - use isEmptyCell to tell.
 */
export const EMPTY_CELL = '';

/**
 * Excel reference of a cell, e.g. "C7".
 *
 * @param {number} rowIndex zero based index of the row
 * @param {number} columnIndex zero based index of the column
 * @returns {string} the reference of the cell, the way excel shows it
 */
export function getCellReference(rowIndex: number, columnIndex: number): string {
  let columnName = '';
  let remaining = columnIndex;

  do {
    columnName =
      String.fromCharCode(FIRST_LETTER_CODE + (remaining % LETTERS_IN_ALPHABET)) + columnName;
    remaining = Math.floor(remaining / LETTERS_IN_ALPHABET) - 1;
  } while (remaining >= 0);

  return `${columnName}${rowIndex + 1}`;
}

/**
 * Whether a cell holds nothing.
 * A cell left out of a row and one written empty by an export count as empty as well.
 *
 * @param {XlsxCellValue} value the value of the cell
 * @returns {boolean} whether the cell holds nothing but spaces
 */
export function isEmptyCell(value: XlsxCellValue): boolean {
  return value === undefined || value === null || String(value).trim() === '';
}

/**
 * Whether a row holds nothing.
 *
 * @param {XlsxRow} row the row of a sheet
 * @returns {boolean} whether every cell of the row is empty
 */
export function isEmptyRow(row: XlsxRow): boolean {
  return row.every((cell) => isEmptyCell(cell));
}

/**
 * The number held by a cell.
 * The parsing is lenient, since the value may come as text:
 * spaces (thousand separators), a unit suffix and a decimal comma are all accepted.
 *
 * @param {XlsxCellValue} value the value of the cell
 * @returns {number | undefined} the number, or undefined when the cell holds no number
 */
export function parseCellNumber(value: XlsxCellValue): number | undefined {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : undefined;
  }
  if (typeof value !== 'string') {
    return undefined;
  }

  const text = value.trim();
  const cleaned = DOT_GROUPED_THOUSANDS.test(text.replace(/\s/g, ''))
    ? text.replace(/[\s.]/g, '')
    : text.replace(NON_NUMBER_CHARS, '').replace(',', '.');

  return NUMBER_PATTERN.test(cleaned) ? Number(cleaned) : undefined;
}
